import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  deleteCustomizationChildrenApi,
  getCustomizationChildrenApi,
  postCustomizationChildrenApi,
  updateCustomizationChildrenApi,
} from "../../api/common/CustomizationChildrenApi";

const initialState = {
  customizationChild: null,
  customizationChildren: [],
  loading: false,
  saving: false,
  deleting: false,
  error: null,
  success: false,
};

const getErrorPayload = (err) => ({
  errorMessage:
    err?.data?.message ||
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    "Something went wrong",
});

const getResponsePayload = (payload) => payload?.response ?? payload;

const getCustomizationChildrenItems = (payload) => {
  const response = getResponsePayload(payload);

  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.customizationChildren)) {
    return response.customizationChildren;
  }
  if (Array.isArray(response?.children)) return response.children;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.content)) return response.content;

  return response ? [response] : [];
};

export const getCustomizationChildren = createAsyncThunk(
  "customizationChildren/getCustomizationChildren",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await getCustomizationChildrenApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  },
);

export const postCustomizationChildren = createAsyncThunk(
  "customizationChildren/postCustomizationChildren",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await postCustomizationChildrenApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  },
);

export const updateCustomizationChildren = createAsyncThunk(
  "customizationChildren/updateCustomizationChildren",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateCustomizationChildrenApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  },
);

export const deleteCustomizationChildren = createAsyncThunk(
  "customizationChildren/deleteCustomizationChildren",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await deleteCustomizationChildrenApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  },
);

const customizationChildrenSlice = createSlice({
  name: "customizationChildren",
  initialState,
  reducers: {
    clearCustomizationChildrenError: (state) => {
      state.error = null;
    },
    clearCustomizationChildrenState: (state) => {
      state.customizationChild = null;
      state.customizationChildren = [];
      state.loading = false;
      state.saving = false;
      state.deleting = false;
      state.error = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCustomizationChildren.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(getCustomizationChildren.fulfilled, (state, action) => {
        state.customizationChildren = getCustomizationChildrenItems(
          action?.payload,
        );
        state.loading = false;
        state.success = true;
      })
      .addCase(getCustomizationChildren.rejected, (state, action) => {
        state.loading = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(postCustomizationChildren.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.success = false;
      })
      .addCase(postCustomizationChildren.fulfilled, (state, action) => {
        const child = getResponsePayload(action?.payload);

        state.customizationChild = child;
        if (child && !Array.isArray(child)) {
          state.customizationChildren = [...state.customizationChildren, child];
        }
        state.saving = false;
        state.success = true;
      })
      .addCase(postCustomizationChildren.rejected, (state, action) => {
        state.saving = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(updateCustomizationChildren.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateCustomizationChildren.fulfilled, (state, action) => {
        const child = getResponsePayload(action?.payload);

        state.customizationChild = child;
        if (child?.id) {
          state.customizationChildren = state.customizationChildren.map((item) =>
            item?.id === child.id ? child : item,
          );
        }
        state.saving = false;
        state.success = true;
      })
      .addCase(updateCustomizationChildren.rejected, (state, action) => {
        state.saving = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(deleteCustomizationChildren.pending, (state) => {
        state.deleting = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteCustomizationChildren.fulfilled, (state, action) => {
        const deletedId = action?.meta?.arg?.id ?? action?.meta?.arg;

        state.customizationChildren = state.customizationChildren.filter(
          (item) => String(item?.id) !== String(deletedId),
        );
        state.deleting = false;
        state.success = true;
      })
      .addCase(deleteCustomizationChildren.rejected, (state, action) => {
        state.deleting = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      });
  },
});

export const {
  clearCustomizationChildrenError,
  clearCustomizationChildrenState,
} = customizationChildrenSlice.actions;

export default customizationChildrenSlice.reducer;
